import { ModelRow, StringUtil, DateUtil, DatePart, Api } from "@mcleod/core";
import { AutogenLayoutServiceIncidentListDetail } from "./autogen/AutogenLayoutServiceIncidentListDetail";
import { DataSourceExecutionEvent, DataSourceAction, Textbox, LookupModelSelectionEvent, BlurEvent, TableRow, DataSourceMode,  DataSourceModeChangeEvent,  TableRowDisplayEvent, ContextMenuCreationEvent } from "@mcleod/components";
import { ModelMovementOrderLookup } from "./models/ModelMovementOrderLookup";
import { LMECompanySettings, LMEUserSettings } from "@mcleod/general";
import { CommonTooltips } from "@mcleod/common";

export class ServiceIncidentListDetail extends AutogenLayoutServiceIncidentListDetail {
    private _firstOpen: boolean = true
    private _orderId: string = ""
    private _shipperStopId: string = ""
    private _consigneeStopId: string = ""
    private _isLTL: boolean = false

    override onLoad() {
        this.clearAllFields();
        this._isLTL = LMECompanySettings.getSingleton().isLtl();
    }

    /** This is an event handler for the onContextMenuCreation event of dataheader1. */
    dataheader1OnContextMenuCreation(event: ContextMenuCreationEvent) {
        if (this.mainDataSource?.activeRow == null || this.mainDataSource.mode !== DataSourceMode.UPDATE) {
            event.preventDefault();
            return;
        }
        event.contextMenu.updateMenuLabels({
            approveIncident: () => ({
                visible: this.activeRow.getBoolean("review_reqd"),
                onClick: () => this.approveIncident()
            })
        });
    }

    approveIncident() {
        const user = LMEUserSettings.getUserLookupModelData()
        this.mainDataSource.activeRow.setLookupModelData("reviewed_by", user)
        this.mainDataSource.activeRow.set("reviewed_by", user.get("id"))
        this.mainDataSource.activeRow.set("reviewed_date", new Date())
        this.mainDataSource.activeRow.set("review_reqd", "N")
    }

    /** This is an event handler for the beforeExecution event of sourceServiceIncidentList. */
    sourceServiceIncidentListBeforeExecution(event: DataSourceExecutionEvent) {
        if(event.getAction() == DataSourceAction.ADD && this.textboxStopNo.lookupModelData) {
            this.mainDataSource.activeRow.set("stop_no", this.textboxStopNo.lookupModelData[0].get("movement_sequence"))
        }
    }

    /** This is an event handler for the afterExecution event of sourceServiceIncidentList. */
    sourceServiceIncidentListAfterExecution(event: DataSourceExecutionEvent) {
        if(event.getAction() == DataSourceAction.SEARCH) {
            this.sourceServiceIncidentStop.search({
                order_id: this.mainDataSource.activeRow.get("order_id"),
                movement_id: this.mainDataSource.activeRow.get("movement_id")
            }).then(() => {
                this.tableStops.rows.forEach((row: TableRow) => row.data.get("id") == this.mainDataSource.activeRow.get("stop_id") ? row.backgroundColor = "success.lightest" : row.backgroundColor = "")
            })
            this.sourceServiceIncidentPostHist.search({
                order_id: this.mainDataSource.activeRow.get("order_id"),
            })
            this.sourceServiceIncidentOrders.search({id: this.textboxOrderId.text}).then((r) => {
                r = r.getSingleModelRow()
                this.textboxBol.text = r.get("blnum")
            })
            this.textboxCausedByTypeAfterDropdownSelection()
        }
    }

    /** This is an event handler for the afterModeChange event of sourceServiceIncidentList. */
    sourceServiceIncidentListAfterModeChange(event: DataSourceModeChangeEvent) {
        if(event.newMode == DataSourceMode.ADD) {
            this.textboxMovementId.enabled = false
            this.textboxStopNo.enabled = false
            this.textboxDelayCode.enabled = false
            this.textboxCausedByType.enabled = false
            this.textboxOrderId.focus()
            this.tableStops.clearRows()
            this.tableHistory.clearRows()
            this.mainDataSource.activeRow.set("caused_by_type", "N")
            this.textboxCausedByTypeAfterDropdownSelection()
            this._firstOpen = false
        } else if(event.newMode == DataSourceMode.UPDATE) {
            this.textboxMovementId.enabled = true
            this.textboxStopNo.enabled = true
            this.textboxDelayCode.enabled = true
            this.textboxCausedByType.enabled = true
            this._firstOpen = false
        }
        this.setTooltipContent();
    }

    clearAndHideCausedByFields() {
        this.clearAndHideField(this.textboxDriverId)
        this.clearAndHideField(this.textboxTractorId)
        this.clearAndHideField(this.textboxTrailerId)
        this.clearAndHideField(this.textboxFleetManager)
        this.clearAndHideField(this.textboxOverridePayeeId)
        this.clearAndHideField(this.textboxUserId)
        this.clearAndHideField(this.textboxName)
        this.clearAndHideField(this.textboxTerminalId)
        this.switchFaultOfCarrierOrDriver.checked = false
        this.switchFaultOfCarrierOrDriver.enabled = false
    }

    clearAndHideField(tb: Textbox) {
        tb.clear()
        this.mainDataSource.activeRow.set(tb.field, null)
        tb.visible = false
        tb.enabled = false
        tb.required = false
    }

    showAndRequireField(tb: Textbox) {
        tb.visible = true
        tb.enabled = true
        tb.required = true
    }

    enableAndSetFaultOfSwitch() {
        this.switchFaultOfCarrierOrDriver.enabled = true
        this.switchFaultOfCarrierOrDriver.checked = true
    }

    /** This is an event handler for the afterDropdownSelection event of textboxCausedByType. */
    textboxCausedByTypeAfterDropdownSelection() {
        if(!this.mainDataSource.activeRow) return
        // Set required/enabled fields based on caused by
        if(!this._firstOpen) {
            this.clearAndHideCausedByFields()
        }
        switch(this.mainDataSource.activeRow.get("caused_by_type")) {
            case "D": //driver
                this.textboxTractorId.visible = true
                this.textboxTrailerId.visible = true
                this.textboxFleetManager.visible = true
            case "O": //other driver
                this.showAndRequireField(this.textboxDriverId)
                this.enableAndSetFaultOfSwitch()
                break
            case "U": //user
                this.showAndRequireField(this.textboxUserId)
                break
            case "E": //other employee
                this.showAndRequireField(this.textboxName)
                break
            case "C": //carrier
                this.showAndRequireField(this.textboxOverridePayeeId)
                this.enableAndSetFaultOfSwitch()
                break
            case "K": //terminal, LTL only
                this.showAndRequireField(this.textboxTerminalId)
                break
            default: //unknown
                break
        }
    }

    /** This is an event handler for the onBlur event of textboxOrderId. */
    textboxOrderIdOnBlur(event: BlurEvent) {
        const orderId = this.textboxOrderId.text.trim()
        this.clearAllFields()
        if(orderId) {
            this.textboxOrderId.text = this.mainDataSource.activeRow.get("order_id")

            //check order exists and has movement(s)
            if (StringUtil.isEmptyString(orderId)) return;

            this.sourceServiceIncidentOrders.search({id: orderId}).then(async (r) => {
                r = r.getSingleModelRow()

                if (r == null) {
                    this.textboxOrderId.validationWarning = "Specified order does not exist";
                    return;
                }
                if (r.get("loaded_movement_count") == 0) {
                    this.textboxOrderId.validationWarning = "Order has no loaded movements";
                    return;
                }

                this.mainDataSource.activeRow.set("order_id", orderId)
                CommonTooltips.setTooltipContentLayout(this.textboxOrderId, orderId, "lme/dispatch/OrderQuickInfo", { minWidth: 128, minHeight: 140 })
                this.textboxOrderId.validationWarning = "";

                this.textboxBol.text = r.get("blnum")
                this._shipperStopId = r.get("shipper_stop_id")
                this._consigneeStopId = r.get("consignee_stop_id")
                this._orderId = orderId

                this.textboxMovementId.enabled = true

                if (r.get("loaded_movement_count") == 1) {
                    const row = await new ModelMovementOrderLookup().searchSingle({"order_id": orderId, "movement.loaded": "L"}, "lme/dispatch/MovementOrderLookup")
                    this.mainDataSource.activeRow.setLookupModelData("movement_id", row)
                    this.mainDataSource.activeRow.set("movement_id", r.get("first_movement_id"))
                    this.textboxMovementIdOnLookupModelSelection(new LookupModelSelectionEvent(this.textboxMovementId, row))
                    this.textboxStopNo.focus()
                } else {
                    this.textboxMovementId.focus()
                }

                this.sourceServiceIncidentPostHist.search({
                    order_id: orderId,
                })

                const customerRow = new ModelRow("lme/ar/customer-lookup", false, { id: r.get("customer_id"), name: r.get("customer.name") })
                this.mainDataSource.activeRow.setLookupModelData("customer_id", customerRow)
                this.mainDataSource.activeRow.set("customer_id", r.get("customer_id"))
                this.textboxCustomerIdOnLookupModelSelection(new LookupModelSelectionEvent(this.textboxCustomerId, customerRow))
                this.mainDataSource.activeRow.set("review_reqd", r.get("customer.service_failures_review"))
                this.textboxDelayCode.enabled = true
            })
        }
    }

    /** This is an event handler for the onLookupModelSelection event of textboxMovementId. */
    textboxMovementIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if(event.hasSelection()) {
            const selectedRow = event.selection;

            this.sourceServiceIncidentStop.search({
                order_id: this.mainDataSource.activeRow.get("order_id"),
                movement_id: this.mainDataSource.activeRow.get("movement_id")
            })

            this.mainDataSource.activeRow.set("operations_user", selectedRow.get("movement.operations_user"))
            this.mainDataSource.activeRow.set("operations_users.name", selectedRow.get("operations_users.name"))
            this.mainDataSource.activeRow.set("dispatcher_user_id", selectedRow.get("movement.dispatcher_user_id"))
            this.mainDataSource.activeRow.set("dispatcher_users.name", selectedRow.get("dispatcher_users.name"))

            const isBrokerage = selectedRow.get("movement.brokerage", "N")
            const causedByType = this._isLTL ? "N" : (isBrokerage == "Y" ? "C" : "D") //stay unknown if LTL. otherwise carrier if brokerage, else driver
            this.mainDataSource.activeRow.set("caused_by_type", causedByType)
            this.textboxCausedByTypeAfterDropdownSelection()

            switch (causedByType) {
                case "D": //driver
                    const first = selectedRow.get("driver.first_name")
                    const last = selectedRow.get("driver.name")
                    this.mainDataSource.activeRow.setLookupModelData("driver_id", new ModelRow("lme/dispatch/asset/driver-only", false, { id: selectedRow.get("driver.id"), name: last, first_name: first } ))
                    this.mainDataSource.activeRow.set("driver.id", selectedRow.get("driver.id"))
                    this.mainDataSource.activeRow.set("driver_id", selectedRow.get("driver.id"))
                    this.mainDataSource.activeRow.set("driver.name", selectedRow.get("driver.name"))
                    this.mainDataSource.activeRow.set("driver.first_name", selectedRow.get("driver.first_name"))
                    this.mainDataSource.activeRow.set("tractor_id", selectedRow.get("equipment_tractor.equipment_id"))
                    this.mainDataSource.activeRow.set("trailer_id", selectedRow.get("equipment_trailer.equipment_id"))
                    const fleetManagerId = selectedRow.get("driver.fleet_manager", selectedRow.get("movement.fleet_manager"))
                    const fleetManagerName = selectedRow.get("driver_manager_users.name", selectedRow.get("fleet_manager_users.name"))
                    this.mainDataSource.activeRow.set("fleet_manager", fleetManagerId)
                    this.mainDataSource.activeRow.set("driver_manager_users.name", fleetManagerName)
                    break;
                case "C": //carrier
                    this.mainDataSource.activeRow.setLookupModelData("override_payee_id", new ModelRow("lme/powerbroker/carrier-payee", false, { id: selectedRow.get("payee.id"), name: selectedRow.get("payee.name") }))
                    this.mainDataSource.activeRow.set("override_payee_id", selectedRow.get("payee.id"))
                    break;
            }

            this.textboxStopNo.enabled = true
            this.textboxCausedByType.enabled = true
        } else {
            this.textboxStopNo.enabled = false
            this.textboxCausedByType.enabled = false
            this.mainDataSource.activeRow.set("caused_by_type", "N")
            this.textboxCausedByTypeAfterDropdownSelection()
            this.textboxDriverId.clear()
            this.textboxTractorId.clear()
            this.textboxTrailerId.clear()
            this.textboxOverridePayeeId.clear()
            this.tableStops.clearRows()
        }

        this.textboxStopNo.clear()
    }

    /** This is an event handler for the onLookupModelSelection event of textboxStopNo. */
    textboxStopNoOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if(event.hasSelection()) {
            const selectedRow = event.selection;
            const stopId = selectedRow.get("id")

            if(this._isLTL) {
                this.mainDataSource.activeRow.set("caused_by_type", "K") //terminal
                this.textboxCausedByTypeAfterDropdownSelection()

                Api.post("lme/dispatch/brltl/service-incident-terminal", JSON.stringify([{ stop_id: stopId }])).then((r) => {
                    this.mainDataSource.activeRow.setLookupModelData("terminal_id", new ModelRow("lme/dispatch/brltl/terminal-lookup", false, { id: r.data[0].terminal_id, name: r.data[0].terminal_name }))
                    this.mainDataSource.activeRow.set("terminal_id", r.data[0].terminal_id)
                })
            }

            const schedArriveEarly = selectedRow.get("sched_arrive_early")
            const schedArriveLate = selectedRow.get("sched_arrive_late")
            const actualArrival = selectedRow.get("actual_arrival")
            const actualDeparture = selectedRow.get("actual_departure")

            this.mainDataSource.activeRow.set("sched_arrive_early", schedArriveEarly)
            this.mainDataSource.activeRow.set("sched_arrive_late", schedArriveLate)
            this.mainDataSource.activeRow.set("actual_arrival", actualArrival)
            this.mainDataSource.activeRow.set("actual_departure", actualDeparture)

            if(!actualArrival) {
                this.mainDataSource.activeRow.set("minutes_late", 0)
            } else if (schedArriveLate) {
                const minutesLate = DateUtil.dateDiff(DatePart.MINUTE, new Date(actualArrival), new Date(schedArriveLate))
                this.mainDataSource.activeRow.set("minutes_late", minutesLate)
            } else if (schedArriveEarly) {
                const minutesLate = DateUtil.dateDiff(DatePart.MINUTE, new Date(actualArrival), new Date(schedArriveEarly))
                this.mainDataSource.activeRow.set("minutes_late", minutesLate)
            }

            this.mainDataSource.activeRow.set("appt_required", selectedRow.get("appt_required"))

            if(stopId == this._shipperStopId) {
                this.mainDataSource.activeRow.set("stop_type", "SH")
            } else if(stopId == this._consigneeStopId) {
                this.mainDataSource.activeRow.set("stop_type", "CO")
            } else {
                this.mainDataSource.activeRow.set("stop_type", selectedRow.get("stop_type"))
            }
            this.tableStops.rows.forEach((row: TableRow) => row.data.get("id") == stopId ? row.backgroundColor = "success.lightest" : row.backgroundColor = "")
        }
    }

    textboxDelayCodeOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if(event.hasSelection()) {
            const selectedRow = event.selection;
            this.mainDataSource.activeRow.set("edi_code_version", selectedRow.get("version"))
            this.mainDataSource.activeRow.set("edi_element_id", selectedRow.get("element_id"))
        }
    }

    /** This is an event handler for the onLookupModelSelection event of textboxCustomerId. */
    textboxCustomerIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if(event.hasSelection()) {
            const selectedRow = event.selection;

            Api.post("lme/datafusion/customer-partner", JSON.stringify([{ customer_id: selectedRow.get("id") }])).then((r) => {
                if(r.data[0]?.partner_name) {
                    this.mainDataSource.activeRow.set("edi_customer.partner_name", r.data[0].partner_name)
                    this.mainDataSource.activeRow.set("edi_customer.partner_id", r.data[0].partner_id)
                    this.mainDataSource.activeRow.set("edi_customer.version", r.data[0].version)
                } else {
                    this.mainDataSource.activeRow.set("edi_customer.partner_name", "")
                    this.mainDataSource.activeRow.set("edi_customer.partner_id", "")
                    this.mainDataSource.activeRow.set("edi_customer.version", "")
                }
                CommonTooltips.setTooltipContentLayout(this.textboxPartnerName, null, "lme/datafusion/EdiProfileStatusQuickInfo", null,
                    {
                        "partner_id": this.mainDataSource.activeRow.get("edi_customer.partner_id"),
                        "version": this.mainDataSource.activeRow.get("edi_customer.version"),
                    });
            })
        }
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxMovementId. */
    textboxMovementIdBeforeLookupModelSearch(event) {
        event.filter.order_id = this._orderId;
        event.filter["movement.loaded"] = "L"
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxStopNo. */
    textboxStopNoBeforeLookupModelSearch(event) {
        if (this.textboxMovementId?.lookupModelData?.length) event.filter.movement_id = this.textboxMovementId.lookupModelData[0].get("movement_id");
        event.filter.order_sequence != null;
    }

    /** This is an event handler for the beforeLookupModelSearch event of textboxDelayCode. */
    textboxDelayCodeBeforeLookupModelSearch(event) {
        const customerId = this.mainDataSource.activeRow.get("customer_id", null)
        event.filter.customer_id = customerId;
        event.filter.code_type = (StringUtil.isEmptyString(customerId)) ? "1651" : "DelayReason";
        event.filter.use_enabled_flag = "Y";
    }

    clearAllFields() {
        if(!this.mainDataSource.activeRow) return
        this.tabGeneral.findComponentsByType(Textbox).forEach((tb) => {
            if(tb.id != "textboxEnteredUserId" && tb.id != "textboxEnteredDate") tb.clear()
        })
        if (this.textboxCausedByType?.items) this.textboxCausedByType.selectedItem = this.textboxCausedByType.items[0]
        if (this.textboxStatus?.items) this.textboxStatus.selectedItem = this.textboxStatus.items[0]
        this.textboxMovementId.enabled = false
        this.textboxStopNo.enabled = false
        this.textboxDelayCode.enabled = false
        this.textboxCausedByType.enabled = false
        this.tableStops.clearRows()
        this.tableHistory.clearRows()
    }

    setTooltipContent() {
        CommonTooltips.setTooltipContentLayout(this.textboxOrderId, this.mainDataSource.activeRow.get("order_id"), "lme/dispatch/OrderQuickInfo", { minWidth: 128, minHeight: 140 })
        CommonTooltips.setTooltipContentLayout(this.textboxPartnerName, null, "lme/datafusion/EdiProfileStatusQuickInfo", null,
            {
                "partner_id": this.mainDataSource.activeRow.get("edi_customer.partner_id"),
                "version": this.mainDataSource.activeRow.get("edi_customer.version"),
            });
        CommonTooltips.setTooltipContentLayout(this.textboxMovementId, this.mainDataSource.activeRow.get("movement_id"), "lme/powerbroker/MovementQuickInfo", { minWidth: 128, minHeight: 140 })
        CommonTooltips.setTooltipContentLayout(this.textboxStopNo, this.mainDataSource.activeRow.get("stop_id"), "lme/dispatch/StopQuickInfo")
    }

    /** This is an event handler for the onRowDisplay event of tableStops. */
    tableStopsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const orderLabel = tableRow.findComponentById("labelOrderId")
        CommonTooltips.setTooltipContentLayout(orderLabel, tableRow.data.get("order_id"), "lme/dispatch/OrderQuickInfo", { minWidth: 128, minHeight: 140 })
    }
}
